import { useEffect, useState } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { listChannels } from '../api/marketplaceApi';
import type { Channel } from 'types/channel';

export interface UseManageableChannelsResult {
  /** Published channels the current identity holds `channel.manage_content` on, per the
   * server-computed `can_manage_content` field (EAINT-862 Phase 4 — see
   * GET /channels/list?includeCanManageContent=true). Never client-derived; fails closed
   * to an empty list on error, same as the SpiceDB check itself fails closed per-row. */
  channels: Channel[];
  loading: boolean;
  error: boolean;
}

/**
 * Channels the current identity may list content into. Filters server-side-computed
 * `can_manage_content` — the client never re-derives this authz decision itself (frontend
 * permissions Rules 1/4): `mine=true` is tenant ownership, which is neither necessary
 * (collaborators) nor sufficient (non-curated org channels) for `manage_content`.
 */
export function useManageableChannels(): UseManageableChannelsResult {
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!apiClients?.schemaClient || !organizationId) {
      setChannels([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    listChannels(apiClients.schemaClient, {
      contentStatus: 'published',
      mine: false,
      includeCanManageContent: true,
    })
      .then((response) => {
        if (cancelled) return;
        const manageable = (response.channels ?? []).filter((c) => c.can_manage_content === true);
        setChannels(manageable);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed: an unavailable check is not evidence of "no manageable channels" —
        // but an empty offer set is the only safe UI state (the picker must never grant by
        // guessing). The dialog's caller surfaces `error` to explain the empty list.
        setChannels([]);
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiClients?.schemaClient, organizationId]);

  return { channels, loading, error };
}

export default useManageableChannels;
